import type { DiscordChatMessage } from '@ottd/api';

import { activeLocale, intlLocale, tr } from '@/lib/locale';

/**
 * Discord's own grouping rule: consecutive messages by the same author within
 * this window collapse under one avatar/header.
 */
const GROUP_WINDOW_MS = 7 * 60 * 1000;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface MessageGroup {
  kind: 'group';
  key: string;
  author: DiscordChatMessage['author'];
  isBot: boolean;
  /** First message's timestamp; shown in the group header (null when unknown). */
  createdAt: string | null;
  messages: DiscordChatMessage[];
}

export interface DateDivider {
  kind: 'date';
  key: string;
  label: string;
}

export type ChatItem = MessageGroup | DateDivider;

function parse(iso: string | null): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function clock(date: Date): string {
  return date.toLocaleTimeString(intlLocale(activeLocale()), {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Discord's header timestamp: "Today at 14:05", "Yesterday at 09:12", else "18/09/2026 14:05". */
export function discordTimestamp(iso: string | null, now: Date = new Date()): string {
  const date = parse(iso);
  if (!date) return '';
  const dayDelta = Math.round((startOfDay(now) - startOfDay(date)) / DAY_MS);
  if (dayDelta === 0) return tr('common:time.todayAt', { time: clock(date) });
  if (dayDelta === 1) return tr('common:time.yesterdayAt', { time: clock(date) });
  return `${date.toLocaleDateString(intlLocale(activeLocale()))} ${clock(date)}`;
}

/** Discord's date divider label: "18 September 2026". */
export function dividerLabel(date: Date): string {
  return date.toLocaleDateString(intlLocale(activeLocale()), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Short time shown on hover next to a follow-up line inside a group. */
export function hoverTime(iso: string | null): string {
  const date = parse(iso);
  return date ? clock(date) : '';
}

/**
 * Same person? The opaque author key is authoritative when both sides have
 * one; guests without a key fall back to name + avatar. Role colour is
 * deliberately not compared, so a colour change mid-conversation does not
 * split a group.
 */
function sameAuthor(a: DiscordChatMessage, b: DiscordChatMessage): boolean {
  if (a.isBot !== b.isBot) return false;
  if (a.author.key !== null && b.author.key !== null) return a.author.key === b.author.key;
  return a.author.name === b.author.name && a.author.avatar === b.author.avatar;
}

/**
 * Turn the flat, oldest-first message list into Discord's visual structure:
 * a date divider whenever the day changes, and message groups that merge
 * consecutive lines by one author posted within the grouping window. A
 * message without a usable timestamp joins the current group when the
 * author matches and never disturbs the day/window bookkeeping.
 */
export function groupMessages(messages: DiscordChatMessage[]): ChatItem[] {
  const items: ChatItem[] = [];
  let lastDay: number | null = null;
  let group: MessageGroup | null = null;
  let lastAt: Date | null = null;

  for (const message of messages) {
    const at = parse(message.createdAt);

    if (at !== null) {
      const day = startOfDay(at);
      if (day !== lastDay) {
        items.push({ kind: 'date', key: `date-${day}`, label: dividerLabel(at) });
        lastDay = day;
        group = null;
      }
    }

    const withinWindow =
      at === null || lastAt === null || at.getTime() - lastAt.getTime() <= GROUP_WINDOW_MS;
    const previous = group?.messages[group.messages.length - 1];
    const continues = previous !== undefined && withinWindow && sameAuthor(previous, message);

    if (continues && group) {
      group.messages.push(message);
    } else {
      group = {
        kind: 'group',
        key: `group-${message.id}`,
        author: message.author,
        isBot: message.isBot,
        createdAt: at ? message.createdAt : null,
        messages: [message],
      };
      items.push(group);
    }
    if (at !== null) lastAt = at;
  }

  return items;
}
